import React, { useMemo } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { useGPS } from '../../contexts/GPSContext';
import { toTitleCase } from '../../utils/stringUtils';
import { normalizeStatusCategory } from '../../utils/gpsStatusCategories';

const STATUS_LABELS = {
  NEW: 'Nouveau',
  INDETERMINATE: 'En traitement',
  DONE: 'Terminé'
};

const STATUS_ORDER = ['NEW', 'INDETERMINATE', 'DONE'];

const STATUS_COLORS = {
  NEW: '#2f6df0',
  INDETERMINATE: '#f5a623',
  DONE: '#16a866'
};

const GPSbrandChart = ({ className = '' }) => {
  const gps = useGPS();

  const data = useMemo(() => {
    const issues = Array.isArray(gps?.summarySensedIssues) ? gps.summarySensedIssues : [];
    if (issues.length === 0) return [];

    const groupMap = new Map();

    issues.forEach(issue => {
      if (!issue?.brand) return;
      const groupKey = issue.brand;
      const displayLabel = toTitleCase(groupKey);
      const cat = normalizeStatusCategory(issue);
      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, { label: displayLabel, NEW: 0, INDETERMINATE: 0, DONE: 0 });
      }
      const row = groupMap.get(groupKey);
      if (STATUS_ORDER.includes(cat)) {
        row[cat] += 1;
      } else {
        row.INDETERMINATE += 1;
      }
    });

    const rows = Array.from(groupMap.values());
    // Sort by total descending (most to least), like "Statuts par enseigne".
    rows.sort((a, b) => (b.NEW + b.INDETERMINATE + b.DONE) - (a.NEW + a.INDETERMINATE + a.DONE));
    return rows;
  }, [gps?.summarySensedIssues]);

  if (!data.length) {
    return (
      <div className="bg-white dark:bg-[#1e1e1e] p-6 rounded-2xl border border-gray-200 dark:border-gray-900">
        <p className="text-gray-600 dark:text-gray-400 text-center">Aucune donnée par marque.</p>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-[#1e1e1e] p-5 sm:p-6 rounded-2xl border border-gray-200 dark:border-gray-900 ${className}`}>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5] flex-none">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" />
          </svg>
        </span>
        <span className="text-base font-bold text-gray-900 dark:text-white">Statuts par marque</span>
        <div className="ml-auto flex items-center gap-3 text-xs font-semibold text-gray-500 dark:text-gray-400">
          {STATUS_ORDER.map(key => (
            <span key={key} className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: STATUS_COLORS[key] }} />
              {STATUS_LABELS[key]}
            </span>
          ))}
        </div>
      </div>
      <div className="h-[420px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 40 }} barCategoryGap="28%">
            <CartesianGrid strokeDasharray="4 4" stroke="#edeff3" vertical={false} />
            <XAxis dataKey="label" angle={-22} textAnchor="end" interval={0} height={60} tick={{ fontSize: 11.5, fill: '#7c8499' }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9aa1b1' }} axisLine={false} tickLine={false} width={42} />
            <Tooltip
              formatter={(value, name) => [value, STATUS_LABELS[name] || name]}
              cursor={{ fill: 'rgba(91,61,240,0.05)' }}
              contentStyle={{ borderRadius: 10, border: '1px solid #e7e9ef', boxShadow: '0 4px 12px rgba(20,25,40,.08)', fontSize: 12 }}
            />
            {STATUS_ORDER.map((key, i) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="a"
                name={STATUS_LABELS[key]}
                fill={STATUS_COLORS[key]}
                radius={i === STATUS_ORDER.length - 1 ? [5, 5, 0, 0] : 0}
                maxBarSize={46}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GPSbrandChart;
